import { trpc } from "@/lib/trpc";
import { useHomepagePromoQuote } from "@/hooks/useHomepagePromoQuote";
import { useServerNow } from "@/lib/serverClock";

export type HomepagePromotionPricing = {
  pricingMode?: string | null;
  priceLabel?: string | null;
  ticketsCheckoutEnabled?: boolean;
};

const REFRESH_MS = 30_000;

/** Shared policy + cached ticketQuote state for each homepage promotion surface. */
export function useHomepagePromotionPricing(eventSlug: string) {
  const policyQuery = trpc.events.publicPricingPolicy.useQuery({ eventSlug }, {
    retry: false,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const { quote, failed: quoteFailed } = useHomepagePromoQuote(
    eventSlug,
    policyQuery.data?.pricingMode === "paid_internal",
  );
  const now = useServerNow(quote?.serverNow, REFRESH_MS);
  const promoActive = Boolean(quote?.promoActive && (
    !quote.promoEndsAt
    || !quote.serverNow
    || Date.parse(quote.promoEndsAt) > now
  ));
  const quoteUsable = Boolean(
    !quoteFailed
    && quote?.available === true
    && (quote.seatsLeft == null || quote.seatsLeft > 0)
    && (!quote.promoActive || promoActive),
  );
  return {
    policy: (policyQuery.data as HomepagePromotionPricing | undefined) ?? null,
    policyLoading: policyQuery.isLoading,
    policyError: policyQuery.isError,
    quote,
    quoteFailed,
    quoteUsable,
    promoActive,
    serverNow: now,
  };
}
